import type { SupportedLocale } from '@erp/shared';

interface DocumentRoutingTranslation {
  title: string;
  description: string;
  documentTypes: string;
  noTypes: string;
  recipients: string;
  cc: string;
  matched: string;
  missing: string;
  invalid: string;
  duplicateTypes: string;
  channelBlocked: string;
  selectionTitle: string;
  selectionHint: string;
  noOptions: string;
  recommended: string;
  customerAddress: string;
  selectionCount: string;
  selectionLimit: string;
}

export const documentRoutingTranslations: Record<SupportedLocale, DocumentRoutingTranslation> = {
  de: {
    selectionTitle: 'Empfänger auswählen',
    selectionHint:
      'Die erste ausgewählte Adresse ist der Hauptempfänger. Weitere ausgewählte Empfänger erhalten eine Kopie (CC). Prüfen Sie die Auswahl vor jedem Versand.',
    noOptions:
      'Keine auswählbaren Empfänger vorhanden. Sie können einen erlaubten Empfänger manuell eintragen.',
    recommended: 'Empfohlen',
    customerAddress: 'Kunden-E-Mail',
    selectionCount: '{{count}} / {{max}} Empfänger ausgewählt',
    selectionLimit: 'Höchstens {{max}} Empfänger insgesamt. Entfernen Sie zuerst eine Auswahl.',
    channelBlocked:
      'Der E-Mail-Versand ist durch eine Kontaktsperre beim Partner oder Ansprechpartner gesperrt. Prüfen Sie die Kontaktpräferenzen.',
    duplicateTypes: 'Wählen Sie jede Belegart nur einmal.',
    title: 'Belegempfänger nach Zuständigkeit',
    description:
      'Ordnen Sie Belegarten einer Zuständigkeit zu, zum Beispiel Rechnung → Buchhaltung. Beim E-Mail-Versand werden die zugeordneten Ansprechpartner des jeweiligen Partners vorgeschlagen.',
    documentTypes: 'Belegarten für diese Zuständigkeit',
    noTypes: 'Keine Belegarten zugeordnet',
    recipients: 'Belegarten',
    cc: 'Kopie (CC)',
    matched:
      'Empfänger aus den Zuständigkeiten: {{names}}. Prüfen Sie die Adressen vor dem Versand.',
    missing:
      'Für die zugeordneten Zuständigkeiten gibt es bei diesem Partner keinen Ansprechpartner mit gültiger E-Mail-Adresse und erlaubtem E-Mail-Kontakt. Prüfen Sie die Kontaktdaten und Kontaktpräferenzen oder tragen Sie einen erlaubten Empfänger ein.',
    invalid:
      'Bitte prüfen Sie Empfänger, Kopien, Betreff und Nachricht. Verwenden Sie gültige E-Mail-Adressen und höchstens {{count}} Kopien. Mehrere Kopien trennen Sie mit Komma oder Semikolon.',
  },
  en: {
    selectionTitle: 'Select recipients',
    selectionHint:
      'The first selected address is the main recipient. Other selected recipients receive a copy (CC). Check your selection before each send.',
    noOptions:
      'No recipients available for selection. You can enter an allowed recipient manually.',
    recommended: 'Recommended',
    customerAddress: 'Customer email',
    selectionCount: '{{count}} / {{max}} recipients selected',
    selectionLimit: 'No more than {{max}} recipients in total. Remove a selection first.',
    channelBlocked:
      'Email delivery is blocked by a contact restriction for the partner or contact. Check the contact preferences.',
    duplicateTypes: 'Select each document type only once.',
    title: 'Document recipients by responsibility',
    description:
      'Assign document types to a responsibility, for example invoice → accounting. Email delivery suggests the assigned contacts of the respective partner.',
    documentTypes: 'Document types for this responsibility',
    noTypes: 'No document types assigned',
    recipients: 'Document types',
    cc: 'Copy (CC)',
    matched: 'Recipients from responsibilities: {{names}}. Check the addresses before sending.',
    missing:
      'This partner has no contact with a valid email address and permission for email contact for the assigned responsibilities. Check contact details and preferences or enter an allowed recipient.',
    invalid:
      'Check the recipient, copies, subject and message. Use valid email addresses and no more than {{count}} copies. Separate copies with commas or semicolons.',
  },
  fr: {
    selectionTitle: 'Choisir les destinataires',
    selectionHint:
      'La première adresse sélectionnée est le destinataire principal. Les autres destinataires sélectionnés reçoivent une copie (CC). Vérifiez votre sélection avant chaque envoi.',
    noOptions:
      'Aucun destinataire disponible à sélectionner. Vous pouvez saisir manuellement un destinataire autorisé.',
    recommended: 'Recommandé',
    customerAddress: 'E-mail du client',
    selectionCount: '{{count}} / {{max}} destinataires sélectionnés',
    selectionLimit: 'Au maximum {{max}} destinataires au total. Retirez d’abord une sélection.',
    channelBlocked:
      'L’envoi par e-mail est bloqué par une restriction du partenaire ou du contact. Vérifiez les préférences de contact.',
    duplicateTypes: 'Sélectionnez chaque type de document une seule fois.',
    title: 'Destinataires des documents par responsabilité',
    description:
      'Associez des types de documents à une responsabilité, par exemple facture → comptabilité. L’envoi par e-mail propose les contacts correspondants du partenaire concerné.',
    documentTypes: 'Types de documents pour cette responsabilité',
    noTypes: 'Aucun type de document associé',
    recipients: 'Types de documents',
    cc: 'Copie (CC)',
    matched:
      'Destinataires selon les responsabilités : {{names}}. Vérifiez les adresses avant l’envoi.',
    missing:
      'Ce partenaire n’a aucun contact avec une adresse e-mail valide et autorisé pour ce canal dans les responsabilités associées. Vérifiez les coordonnées et préférences ou saisissez un destinataire autorisé.',
    invalid:
      'Vérifiez le destinataire, les copies, l’objet et le message. Utilisez des adresses e-mail valides et au maximum {{count}} copies. Séparez les copies par des virgules ou des points-virgules.',
  },
  es: {
    selectionTitle: 'Seleccionar destinatarios',
    selectionHint:
      'La primera dirección seleccionada es el destinatario principal. Los demás destinatarios seleccionados reciben una copia (CC). Revise la selección antes de cada envío.',
    noOptions:
      'No hay destinatarios disponibles para seleccionar. Puede introducir manualmente un destinatario permitido.',
    recommended: 'Recomendado',
    customerAddress: 'Correo del cliente',
    selectionCount: '{{count}} / {{max}} destinatarios seleccionados',
    selectionLimit: 'Como máximo {{max}} destinatarios en total. Quite primero una selección.',
    channelBlocked:
      'El envío por correo está bloqueado por una restricción del socio o del contacto. Revise las preferencias de contacto.',
    duplicateTypes: 'Seleccione cada tipo de documento una sola vez.',
    title: 'Destinatarios de documentos por responsabilidad',
    description:
      'Asigne tipos de documento a una responsabilidad, por ejemplo factura → contabilidad. El envío por correo propone los contactos asignados del socio correspondiente.',
    documentTypes: 'Tipos de documento para esta responsabilidad',
    noTypes: 'Sin tipos de documento asignados',
    recipients: 'Tipos de documento',
    cc: 'Copia (CC)',
    matched:
      'Destinatarios según las responsabilidades: {{names}}. Compruebe las direcciones antes del envío.',
    missing:
      'Este socio no tiene ningún contacto con correo válido y autorización para este canal en las responsabilidades asignadas. Revise los datos y preferencias o introduzca un destinatario permitido.',
    invalid:
      'Revise el destinatario, las copias, el asunto y el mensaje. Use correos válidos y como máximo {{count}} copias. Separe las copias con comas o punto y coma.',
  },
  tr: {
    selectionTitle: 'Alıcıları seçin',
    selectionHint:
      'İlk seçilen adres ana alıcıdır. Diğer seçilen alıcılara bilgi kopyası (CC) gönderilir. Her gönderimden önce seçiminizi kontrol edin.',
    noOptions: 'Seçilebilecek alıcı yok. İzin verilen bir alıcıyı elle girebilirsiniz.',
    recommended: 'Önerilen',
    customerAddress: 'Müşteri e-postası',
    selectionCount: '{{count}} / {{max}} alıcı seçildi',
    selectionLimit: 'Toplam en fazla {{max}} alıcı seçilebilir. Önce bir seçimi kaldırın.',
    channelBlocked:
      'İş ortağı veya kişi için iletişim kısıtlaması nedeniyle e-posta gönderimi engelleniyor. İletişim tercihlerini kontrol edin.',
    duplicateTypes: 'Her belge türünü yalnızca bir kez seçin.',
    title: 'Sorumluluğa göre belge alıcıları',
    description:
      'Belge türlerini bir sorumluluğa atayın; örneğin fatura → muhasebe. E-posta gönderiminde ilgili iş ortağının atanmış kişileri önerilir.',
    documentTypes: 'Bu sorumluluk için belge türleri',
    noTypes: 'Belge türü atanmamış',
    recipients: 'Belge türleri',
    cc: 'Bilgi (CC)',
    matched: 'Sorumluluklara göre alıcılar: {{names}}. Göndermeden önce adresleri kontrol edin.',
    missing:
      'Bu iş ortağında atanmış sorumluluklar için geçerli e-posta adresi ve e-posta iletişim izni olan bir kişi bulunmuyor. İletişim bilgilerini ve tercihlerini kontrol edin veya izin verilen bir alıcı girin.',
    invalid:
      'Alıcıyı, bilgi alıcılarını, konuyu ve mesajı kontrol edin. Geçerli e-posta adresleri ve en fazla {{count}} bilgi alıcısı kullanın. Adresleri virgül veya noktalı virgülle ayırın.',
  },
};
